import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type AppNameValue,
  type CreateUserRequest,
  type FunctionalRole,
  type UpdateUserRequest,
  type User,
} from '../client';

/** Stable query-key factory for user / catalogue caches. */
export const userKeys = {
  all: ['directory', 'users'] as const,
  list: () => ['directory', 'users', 'list'] as const,
  byId: (id: string) => ['directory', 'users', 'detail', id] as const,
  functionalRoles: (app?: AppNameValue) =>
    ['directory', 'users', 'functional-roles', app ?? 'all'] as const,
};

/**
 * Fetch every user in the caller's account. Drives the Settings → Users
 * screen — full role + functional-role payload comes inline with each row
 * so the screen renders without a per-user round-trip. `staleTime`
 * defaults to 5 minutes; admin lists rarely shift mid-session.
 */
export function useAccountUsers(
  options?: Omit<UseQueryOptions<User[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<User[], Error>({
    queryKey: userKeys.list(),
    queryFn: () => directoryApi.getAccountUsers(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Fetch a single user by id (scoped to the caller's account). */
export function useUserById(
  id: string | null,
  options?: Omit<UseQueryOptions<User, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<User, Error>({
    queryKey: id ? userKeys.byId(id) : userKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No user id');
      return directoryApi.getUserById(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch the functional-role catalogue. Pass `app` to filter to one app
 * (the Settings → Users picker passes the current app); omit for the
 * future cross-app admin screen. The catalogue is effectively static
 * per deploy, so `staleTime` is generous.
 */
export function useFunctionalRoles(
  app?: AppNameValue,
  options?: Omit<UseQueryOptions<FunctionalRole[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FunctionalRole[], Error>({
    queryKey: userKeys.functionalRoles(app),
    queryFn: () => directoryApi.getFunctionalRoles(app),
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/users`. Invalidates the account-wide
 * list on success so the new row shows up immediately.
 */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateUserRequest>({
    mutationKey: [...userKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createUser(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

/**
 * Mutation wrapper around PUT `/users`. Invalidates both the list and
 * the per-id cache on success — the latter for callers using
 * `useUserById` alongside the list (e.g. an edit drawer).
 */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateUserRequest>({
    mutationKey: [...userKeys.all, 'update'],
    mutationFn: (body) => directoryApi.updateUser(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: userKeys.list() });
      qc.invalidateQueries({ queryKey: userKeys.byId(vars.userId) });
    },
  });
}

/**
 * Mutation wrapper around DELETE `/users/{id}`. Caller passes the
 * organisation id alongside the user id so the BE can scope the
 * deletion to one org's app role rather than the whole account.
 */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; organisationId: string }>({
    mutationKey: [...userKeys.all, 'delete'],
    mutationFn: ({ id, organisationId }) =>
      directoryApi.deleteUser(id, organisationId),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: userKeys.list() });
      qc.invalidateQueries({ queryKey: userKeys.byId(id) });
    },
  });
}
